/**
 * trec-svc /trec/form-templates — the brokerage's real form PDFs (V051).
 *
 * A template is the actual PDF that goes into the DocuSign envelope. How its
 * tabs are placed depends on the PDF, and the server decides which at upload:
 *
 *   AUTO_FIELDS  a fillable AcroForm (TREC 20-19, IABS, Seller's Disclosure).
 *                DocuSign converts each named field into a tab, so mapping means
 *                binding those existing names to what we can prefill.
 *   ANCHOR       a FLAT PDF with no fields at all (both Texas REALTORS
 *                agreements). NOTHING exists on it until we say so: each field is
 *                authored by naming literal text in the page ("Client:"), which
 *                occurrence of it, and how far from it the box sits. Leave it
 *                unmapped and the client gets a form with nowhere to type.
 *
 * Upload is two-legged (as with client documents): presign → PUT straight to S3
 * → register, so a 1MB+ PDF never rides the Lambda request path.
 */

import type { Uuid } from "@re-platform/types";

import type { ApiClient } from "../client";

export type TabStrategy = "AUTO_FIELDS" | "ANCHOR";
export type FieldKind = "text" | "checkbox" | "choice" | "signature";
/** Whose line a signature is. Anything not CLIENT is stripped before sending. */
/** CO_CLIENT is the client's spouse (Texas community property) — a second, remote
 *  recipient on the envelope, not a counterparty whose tabs get stripped. */
export type SignerRole = "CLIENT" | "CO_CLIENT" | "BUYER" | "SELLER" | "AGENT";
export type TemplateBrokerageType = "REAL_ESTATE" | "MORTGAGE" | "SHARED";

export interface TemplateField {
  id: Uuid;
  /** AUTO_FIELDS: the AcroForm field name. ANCHOR: a key we chose. */
  pdf_field_name: string;
  field_kind: FieldKind;
  page_number: number | null;
  /** What we prefill it with; null = ships blank for the signer to complete. */
  canonical_field: string | null;
  signer_role: SignerRole | null;
  anchor_string: string | null;
  anchor_occurrence: number;
  x_offset: number;
  y_offset: number;
  /** Resolved server-side from anchor + offset; what the envelope carries. */
  x_position: number | null;
  y_position: number | null;
  /** V093 — the packet may not be sent while this field is blank. */
  is_required: boolean;
  /** V093 — "5.A(1)(a)": where the agent is told to go when a send is refused. */
  paragraph_label: string | null;
}

export interface FormTemplate {
  id: Uuid;
  /** null = the platform-standard form every brokerage gets. */
  tenant_id: Uuid | null;
  form_type: string;
  template_version: string;
  display_name: string;
  s3_key: string;
  brokerage_type: TemplateBrokerageType;
  tab_strategy: TabStrategy;
  page_count: number | null;
  field_count: number | null;
  active: boolean;
  /** V073 — DRAFT | ACTIVE | SUPERSEDED. A DRAFT is never sent to anyone. */
  status: TemplateStatus;
  /** V073 — set when this is ONE AGENT's private override (agents.id). */
  owner_agent_id: Uuid | null;
  created_at: string;
  fields: TemplateField[];
}

export type TemplateStatus = "DRAFT" | "ACTIVE" | "SUPERSEDED";

/** Mirrors trec-svc's TemplateValidationOut. */
export interface TemplateValidation {
  ok: boolean;
  /** Canonical fields this form kind requires that nothing is bound to. */
  missing_required: string[];
  /** Blocking problems, each phrased as something to fix. */
  problems: string[];
  /** Worth a look, but not blocking. */
  warnings: string[];
  status: TemplateStatus;
}

export interface TemplatePreview {
  /** Short-lived presigned GET; regenerated per call, so always current. */
  preview_url: string;
  field_count: number;
}

export interface FieldMappingIn {
  pdf_field_name: string;
  canonical_field?: string | null;
  signer_role?: SignerRole | null;
  field_kind?: FieldKind | null;
  anchor_string?: string | null;
  anchor_occurrence?: number;
  x_offset?: number;
  y_offset?: number;
  /**
   * V093 — the packet may not be sent while this field is blank.
   *
   * 🔴 PATCH /fields REPLACES a row's flags from the body, so omitting this on
   * an edit sets it back to false and silently disarms the completeness gate for
   * that field. Any caller that edits a mapping must send back what it read.
   * (`default_value`, `width` and `height` have always had the same property and
   * are still absent from this interface — see the note below.)
   */
  is_required?: boolean;
  /** V093 — "5.A(1)(a)". Shown to the agent when a send is refused. */
  paragraph_label?: string | null;
}

/**
 * ⚠️ This interface is a SUBSET of what the API accepts. `default_value`,
 * `width`, `height`, `page_number`, `x_position` and `y_position` are all real
 * fields on the server's FieldMappingIn and are missing here — a pre-existing
 * gap, not one introduced with V093, and the reason the catalog is loaded by
 * `scripts/ops/load-form-templates.sh` rather than through this client. Widening
 * it is worth doing, but it is a change to the mapping UI's contract and belongs
 * in its own piece of work.
 */

/** What the server can actually fill — the mapping UI offers nothing else. */
export interface CanonicalFields {
  party: string[];
  document: string[];
}

export const formTemplateService = {
  list(client: ApiClient, params?: { include_inactive?: boolean }) {
    return client.request<FormTemplate[]>("/trec/form-templates", {
      query: params,
    });
  },

  canonicalFields(client: ApiClient) {
    return client.request<CanonicalFields>(
      "/trec/form-templates/canonical-fields",
    );
  },

  uploadUrl(
    client: ApiClient,
    body: { form_type: string; template_version: string; filename: string },
  ) {
    return client.request<{ upload_url: string; s3_key: string }>(
      "/trec/form-templates/upload-url",
      { method: "POST", body },
    );
  },

  /**
   * Register the uploaded object: the server reads it back out of S3,
   * introspects it, and decides AUTO_FIELDS vs ANCHOR. Supersedes the incumbent
   * active template for the same form type + scope.
   */
  register(
    client: ApiClient,
    body: {
      form_type: string;
      template_version: string;
      display_name: string;
      s3_key: string;
      brokerage_type?: TemplateBrokerageType;
    },
  ) {
    return client.request<FormTemplate>("/trec/form-templates", {
      method: "POST",
      body,
    });
  },

  /**
   * Why this template can or cannot go live (V073). Read-only — the same check
   * `activate` enforces, so the mapping UI can show what's still missing WHILE
   * the agent works rather than only when they try to publish.
   */
  validation(client: ApiClient, templateId: Uuid) {
    return client.request<TemplateValidation>(
      `/trec/form-templates/${templateId}/validation`,
    );
  },

  /**
   * Test-fill with obviously-fake data and return a link to look at it.
   * The completeness rules can prove a required field is bound to SOMETHING;
   * only a human looking at a filled copy can tell it's the RIGHT box.
   */
  preview(client: ApiClient, templateId: Uuid) {
    return client.request<TemplatePreview>(
      `/trec/form-templates/${templateId}/preview`,
      { method: "POST" },
    );
  },

  /** Promote a DRAFT to live. 422s with the specific blockers if not ready. */
  activate(client: ApiClient, templateId: Uuid) {
    return client.request<FormTemplate>(
      `/trec/form-templates/${templateId}/activate`,
      { method: "POST" },
    );
  },

  updateFields(client: ApiClient, templateId: Uuid, fields: FieldMappingIn[]) {
    return client.request<TemplateField[]>(
      `/trec/form-templates/${templateId}/fields`,
      { method: "PATCH", body: { fields } },
    );
  },
};

/**
 * PUT the PDF straight to S3. `Content-Type: application/pdf` is part of the
 * presigned signature — send anything else and S3 rejects it.
 */
export async function uploadTemplatePdf(
  uploadUrl: string,
  file: Blob,
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": "application/pdf" },
    body: file,
  });
  if (!response.ok) {
    throw new Error(`S3 upload failed with status ${response.status}`);
  }
}

/**
 * The ONE envelope a client signs (V051) — and the agent's fix path.
 *
 * A client who wants a change ("that's not my address", "I won't agree to
 * intermediary status") used to be stuck: the agreement was already sent. The agent
 * now edits the form's fields (`formService.updateFields`) and resends. The live
 * DocuSign envelope is VOIDED and replaced by one carrying the corrected values, on
 * the same template revision the client first read — so they can never sign a
 * version the agent has since corrected, and the voided envelope stays in DocuSign
 * as the record of what was originally sent.
 */
export interface FormPacket {
  id: Uuid;
  client_id: Uuid;
  /** The CURRENT envelope — a resend voids the old one and mints a new id here. */
  docusign_envelope_id: string | null;
  status: string;
  sent_at: string | null;
  signed_at: string | null;
}

export const packetService = {
  /**
   * Release a HELD packet — every form in it goes out as ONE envelope.
   *
   * A packet containing a form the agent must complete sends nothing at dossier
   * completion, so the client gets one signing ceremony instead of two. This is
   * the agent's Send. Deliberately NOT `formService.send`, which sends a single
   * form and would hand the client back the several ceremonies the hold exists
   * to prevent. 409 if the packet already has an envelope — that is `resend`.
   */
  send(client: ApiClient, packetId: Uuid) {
    return client.request<FormPacket>(`/trec/packets/${packetId}/send`, {
      method: "POST",
    });
  },
  /** 409 once the packet is SIGNED — an executed contract is not a draft. */
  resend(client: ApiClient, packetId: Uuid) {
    return client.request<FormPacket>(`/trec/packets/${packetId}/resend`, {
      method: "POST",
    });
  },
};
